import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import H from "history";
import Table, { TableColumn } from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import { IRootState } from '../../../store';
import { TableRowProps } from '../../TableRow';
import { cellForColumn } from './SubmissionAssignmentAnalyticsUtils';
import { AssignmentSubject, TeacherAssignmentSummary } from '../../../models/analytics/AssignmentSubmissionSummaries';
import AcademicYearPicker from '../../AcademicYearPicker';
import { openFileDownload, parseQueryString } from '../../../utils/Utils';
import TeacherSubmissionResponse from "../../../models/analytics/TeacherSubmissionResponse";
import SimpleDoughnut from "../../assignment-analytics/SimpleDoughnut";
import { AnalyticsDoughnutPalette } from "../../assignment-analytics/AnalyticsDoughnutUtils";
import PercentageOverviewDoughnut from "../../assignment-analytics/PercentageOverviewDoughnut";
import SchoolMenu from '../../SchoolMenu';
import { CommandBarButton } from 'office-ui-fabric-react/lib-commonjs';
import SchoolPageTitle from '../../SchoolPageTitle';
import { Dropdown, TextField } from 'office-ui-fabric-react';
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";

interface SchoolTeacherSubmissonAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
    selectedSubject: string;
}

interface SchoolTeacherSubmissonAnalyticsPageState {
    result?: TeacherSubmissionResponse;
    filteredResults?: TeacherAssignmentSummary[];
    subjects?: AssignmentSubject[];
    filter?: string
}

class SchoolTeacherSubmissonAnalyticsPage extends Component<SchoolTeacherSubmissonAnalyticsPageProps & DispatchProps, SchoolTeacherSubmissonAnalyticsPageState> {
    static displayName = SchoolTeacherSubmissonAnalyticsPage.name;

    constructor(props: SchoolTeacherSubmissonAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = { filter: "" }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear) {
            this.getSubjectsList();
            await this.getResult();
            this.filterResults();
        }
    }

    async componentDidUpdate(prevProps: Readonly<SchoolTeacherSubmissonAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear) {
            await this.getResult();
            this.filterResults();
        }

        if (this.props.selectedSubject !== prevProps.selectedSubject) {
            this.filterResults();
            await this.getResult();
        }

        if (this.state.filter !== prevState.filter) {
            this.filterResults();
        }
    }

    private updateSubjectParams(subject: string) {
        var queryObject = parseQueryString(this.props.history.location.search);
        queryObject["subject"] = subject;
        this.props.history.replace({ search: ApiService.buildQueryString(queryObject) });
    }

    private async getSubjectsList(): Promise<void> {
        this.setState({
            subjects: undefined
        })

        const subjects = await this.props.apiService.listAssignmentSubjects(this.props.tenantId, this.props.schoolCode, this.props.academicYear);

        this.setState({
            subjects
        })
    }

    private async getResult(): Promise<void> {
        this.setState({
            result: undefined
        });

        const result = await this.props.apiService.getTeacherSubmissionsAnalyticsForSchool(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!, this.props.selectedSubject);

        this.setState({
            result
        });
    }

    private filterResults = () => {
        this.setState({
            filteredResults: this.state.result?.items.filter(this.filterResult)
        });
    }

    private filterResult = (item: TeacherAssignmentSummary) => {
        if (this.state.filter) {
            return item.teacherDisplayName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1 ||
                item.teacherUserPrincipalName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1;
        }
        else {
            return true;
        }
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private async export(): Promise<void> {
        var result = await this.props.apiService.getTeacherSubmissionsAnalyticsForSchoolExport(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!, this.props.selectedSubject)
        openFileDownload(result);
    }

    private static readonly columns: TableColumn[] = [
        { title: "Teacher", propertyPath: "teacherDisplayName", extraDetail: "User principal name", enableSort: true, flex: 3 },
        { title: "Classes with Assignments", propertyPath: "classesWithAssignmentsPercentage", extraDetail: "Classes with at least one assignment set by this teacher", enableSort: true, flex: 2 },
        { title: "Assignments Set", propertyPath: "assignmentsTotal", extraDetail: "Total assignments set by this teacher", enableSort: true, flex: 1 }
    ];

    private readonly rowProps = (item: TeacherAssignmentSummary): TableRowProps => {
        return {
            enableRowLink: false
        };
    }

    private buildSubjectsDropdown(): JSX.Element {
        if (this.state.subjects === undefined) {
            return <Dropdown
                className="filter-controls__input"
                selectedKey={""}
                options={[{ key: "", text: "Loading..." }]}
                styles={{ dropdown: { width: 162 } }}
            />
        }
        else {

            var subjectOptions = this.state.subjects
                .sort((a, b) => a.subjectName.toLowerCase() > b.subjectName.toLowerCase() ? 1 : 0)
                .map(subject => {
                    return {
                        key: `${subject.subjectCode}`,
                        text: subject.subjectName,
                    }
                });

            subjectOptions.splice(0, 0, { key: "", text: "All Subjects"});

            return <Dropdown
                className="filter-controls__input"
                selectedKey={this.props.selectedSubject}
                onChange={(event, newValue) => this.updateSubjectParams(newValue!.key as string)}
                options={subjectOptions}
                styles={{ dropdown: { width: 162 } }}
            />
        }
    }

    private buildResults(): JSX.Element {
        if (this.state.result === undefined || this.state.filteredResults === undefined) {
            return <LoadingCard />;
        }
        else {
            return <Fragment>
                <Table
                    title={"Teachers"}
                    items={this.state.filteredResults}
                    columns={SchoolTeacherSubmissonAnalyticsPage.columns}
                    cellForColumn={cellForColumn}
                    rowPropsForItem={this.rowProps}
                    defaultSortColumnPath={'teacherDisplayName'}
                    enableSort={true}>

                    {this.buildSubjectsDropdown()}

                    <TextField className="filter-controls__input"
                        placeholder="Filter"
                        value={this.state.filter}
                        onChange={(event, newValue) => this.setState({ filter: newValue })} />

                    <CommandBarButton
                        iconProps={{ iconName: "DownloadDocument" }}
                        text="Export All"
                        onClick={() => this.export()} />
                </Table>
            </Fragment>
        }
    }

    private buildCharts = (): JSX.Element | undefined => {

        if (this.state.result === undefined) {
            return (<LoadingCard />);
        }

        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{ marginTop: "0.25rem" }}>
                <div className={"number-stat"}>
                    <div className="number-stat__number number-stat__number--small">
                        {this.state.result.assignmentsSetTotal}
                    </div>
                    <div className={"number-stat__description"}>Assignments Set</div>
                </div>

                <SimpleDoughnut
                    title={"Teachers who have set assignments"}
                    aValue={this.state.result.haveSetAssignmentsPercentage} aLabel={"Have set assignments"} aColour={AnalyticsDoughnutPalette.green}
                    bValue={this.state.result.haveNotSetAssignmentsPercentage} bLabel={"Have not set any assignments"} bColour={AnalyticsDoughnutPalette.grey} />

                <PercentageOverviewDoughnut percentageOverview={this.state.result.classCoveragePercentageOverview} title={"Teachers setting assignments for all their classes"} labels={PercentageOverviewDoughnut.LABELS_ALL} />
            </div>
        );
    }

    render() {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Assignment Analytics - Teachers" reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix="Teachers" />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} current={"teacher"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>

                    {this.buildCharts()}
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SchoolTeacherSubmissonAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);
        const selectedSubject = parseQueryString(ownProps.history.location.search)["subject"] ?? "";

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear,
            selectedSubject: selectedSubject
        };
    }
)(SchoolTeacherSubmissonAnalyticsPage)